import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-6">
              <i className="ri-book-2-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-3">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.categorySlug === comic.categorySlug || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫网免费在线阅读`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    image: comic.coverImage,
    url: `https://hanmanwang.com/comic/${comic.id}`,
    publisher: {
      '@type': 'Organization',
      name: '韩漫网',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: `${comic.category}韩漫`, path: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          {/* Comic Detail */}
          <article className="py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Cover */}
              <div className="flex-shrink-0">
                <div className="w-56 md:w-64 aspect-[3/4] rounded-2xl overflow-hidden bg-background-100 border border-background-200/70">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 作者${comic.author}`}
                    title={`${comic.title} - 韩漫网免费在线阅读`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {comic.isHot && (
                    <span className="px-2.5 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-md whitespace-nowrap">
                      热门推荐
                    </span>
                  )}
                  {comic.isNew && (
                    <span className="px-2.5 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-md whitespace-nowrap">
                      新番上线
                    </span>
                  )}
                  <span className="px-2.5 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-md whitespace-nowrap">
                    {comic.status}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-4">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-5 text-sm">
                  <div className="flex items-center gap-1.5 text-foreground-600">
                    <i className="ri-user-line text-foreground-400"></i>
                    <span>作者：{comic.author}</span>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <div className="flex items-center gap-1.5 text-foreground-600">
                    <i className="ri-folder-line text-foreground-400"></i>
                    <Link to={`/category/${comic.categorySlug}`} className="text-primary-600 hover:text-primary-700">
                      {comic.category}
                    </Link>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-amber-400"></i>
                    <span className="text-foreground-700 font-medium">{comic.rating}</span>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <div className="flex items-center gap-1.5 text-foreground-600">
                    <i className="ri-eye-line text-foreground-400"></i>
                    <span>{comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万次阅读` : `${comic.views}次阅读`}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 text-xs bg-background-100 text-foreground-600 rounded-md border border-background-200/60 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6 border border-background-200/70">
                  <h2 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex items-center gap-3">
                  <button className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line"></i> 开始阅读
                  </button>
                  <button className="px-4 py-3 bg-background-100 hover:bg-background-200 text-foreground-600 text-sm rounded-lg transition-colors border border-background-200/70 cursor-pointer w-11 h-11 flex items-center justify-center">
                    <i className="ri-bookmark-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Chapter List */}
            <section className="mt-10">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-foreground-950 font-heading">
                  章节<strong className="text-primary-500 font-bold">列表</strong>
                </h2>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1.5 text-xs font-medium bg-primary-500 text-background-50 rounded-md whitespace-nowrap cursor-pointer">
                    正序
                  </button>
                  <button className="px-3 py-1.5 text-xs font-medium bg-background-100 text-foreground-600 rounded-md whitespace-nowrap cursor-pointer">
                    倒序
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {[...comic.chapters]
                  .sort((a, b) => b.number - a.number)
                  .map((chapter) => (
                    <Link
                      key={chapter.id}
                      to={`/comic/${comic.id}`}
                      className="flex items-center justify-between p-3.5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-background-50 transition-all duration-200 group"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs font-medium text-foreground-500 w-14 text-right flex-shrink-0">
                          第{chapter.number}话
                        </span>
                        <span className="text-sm text-foreground-800 truncate group-hover:text-primary-600 transition-colors">
                          {chapter.title}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.updatedAt}</span>
                    </Link>
                  ))}
              </div>
            </section>

            {/* Related Comics */}
            {relatedComics.length > 0 && (
              <section className="mt-14 pb-14">
                <h2 className="text-xl font-bold text-foreground-950 font-heading mb-6">
                  相关<strong className="text-primary-500 font-bold">推荐</strong>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop="true">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}